import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import * as ib from "@/api/identity-board";

// ═══════════════════════════════════════════════════════════════════
// What the board KNOWS about itself — and the nudge loop that acts on it.
//
// None of this had a surface. The counts, the vocabularies a board is built
// from, and the entire nudge lifecycle were reachable only in theory.
//
// Three things worth seeing rather than reading:
//
//   · Discovery is GATED on the board earning it. `discoveryUnlocked` false
//     means the matches list is empty for a reason, not broken.
//   · A count endpoint exists separately from the list so a filter can be
//     answered BEFORE paging — "narrowing to Gujarati leaves you four" is
//     worth knowing before the list reloads, not after.
//   · A nudge has four outcomes and they are four on purpose. Shown-and-
//     ignored is not dismissed-on-purpose, and clicked-but-never-finished is
//     neither. Collapse them and the completion rate can no longer tell
//     "nobody wanted this" from "nobody could finish it".
// ═══════════════════════════════════════════════════════════════════

const TABS = ["board", "counts", "nudges", "reference"] as const;
type Tab = (typeof TABS)[number];

export default function BoardSignalsLab() {
  const [tab, setTab] = useState<Tab>("board");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Board signals"
        subtitle="What the board knows about itself, and the nudges that act on it."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Identity Board", href: "/lab/board" },
          { label: "Signals" },
        ]}
      />

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex gap-1 flex-wrap mb-4 border-b border-gray-200 pb-3">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-lg text-sm capitalize ${
                tab === t ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "board" && <MyBoard />}
        {tab === "counts" && <Counts />}
        {tab === "nudges" && <Nudges />}
        {tab === "reference" && <Reference />}
      </section>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div>
      <div className="text-2xl font-semibold text-gray-900 tabular-nums">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
      {hint && <div className="text-xs text-gray-400">{hint}</div>}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-gray-500">{children}</p>;
}

// ── My board ───────────────────────────────────────────────────────

function MyBoard() {
  const { data, isLoading } = useQuery({
    queryKey: ["ib-me"],
    queryFn: ib.getMyBoard,
  });

  if (isLoading) return <Empty>Loading…</Empty>;
  const b = data?.data;

  // null is a real answer: this person never started one.
  if (!b) {
    return (
      <Empty>
        No board yet — the endpoint returns <code>null</code>, which is not an
        error and not an empty object. Start one from the Board pages.
      </Empty>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-6 flex-wrap">
        <Stat label="blocks completed" value={`${b.blocksCompleted} / 4`} />
        <Stat label="completeness" value={`${b.completenessScore}%`} />
        <Stat label="match radius" value={`${b.matchRadiusMiles} mi`} />
      </div>

      <div className="flex gap-2 flex-wrap">
        <Badge variant={b.discoveryUnlocked ? "green" : "yellow"}>
          discovery {b.discoveryUnlocked ? "unlocked" : "locked"}
        </Badge>
        <Badge variant={b.isDiscoverable ? "green" : "gray"}>
          {b.isDiscoverable ? "discoverable" : "hidden"}
        </Badge>
        <Badge variant="blue">visible to {b.discoverableBy.replace(/_/g, " ")}</Badge>
        {b.boardTheme && <Badge variant="purple">{b.boardTheme}</Badge>}
      </div>

      {!b.discoveryUnlocked && (
        <p className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
          Discovery is gated on the board earning it. While this is locked an
          empty match list is the correct answer, not a fault.
        </p>
      )}

      <div className="flex gap-2 flex-wrap text-sm">
        {(["core", "journey", "interests", "lifestyle"] as const).map((block) => (
          <Badge key={block} variant={b[block] ? "green" : "gray"}>
            {block}: {b[block] ? "filled" : "empty"}
          </Badge>
        ))}
      </div>
    </div>
  );
}

// ── Counts ─────────────────────────────────────────────────────────

function Counts() {
  const [language, setLanguage] = useState("");
  const [originState, setOriginState] = useState("");

  const filters = {
    language: language.trim() || undefined,
    originState: originState.trim() || undefined,
  };

  const { data: matches, isLoading } = useQuery({
    queryKey: ["ib-match-counts", filters],
    queryFn: () => ib.getMatchCounts(filters),
  });
  const { data: waves } = useQuery({
    queryKey: ["ib-wave-counts"],
    queryFn: ib.getWaveCounts,
  });

  const m = matches?.data;
  const w = waves?.data;

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs text-gray-500 mb-2">
          Counted under the same filters the list takes, so the effect of
          narrowing is answerable before anything reloads.
        </p>
        <div className="flex gap-2 flex-wrap mb-3">
          <input
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            placeholder="Language (Gujarati)"
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
          />
          <input
            value={originState}
            onChange={(e) => setOriginState(e.target.value)}
            placeholder="Origin state (GJ)"
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
          />
          {(language || originState) && (
            <button
              onClick={() => {
                setLanguage("");
                setOriginState("");
              }}
              className="text-xs text-gray-600 underline"
            >
              clear
            </button>
          )}
        </div>
        {isLoading ? (
          <Empty>Loading…</Empty>
        ) : !m ? (
          <Empty>No counts.</Empty>
        ) : (
          <div className="flex gap-6 flex-wrap">
            <Stat label="twins" value={m.twins} />
            <Stat label="strong" value={m.strong} />
            <Stat label="moderate" value={m.moderate} />
            <Stat label="total" value={m.total} />
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm font-medium text-gray-900 mb-1">Waves</p>
        <p className="text-xs text-gray-500 mb-2">
          <strong>Mutual</strong> is the only one that opens a conversation —
          both people waved. <strong>Sent</strong> counts what is still
          outstanding, not what was ever sent: a reciprocated wave becomes
          mutual and leaves this number, so zero sent beside three mutual is
          somebody whose waves were all answered.
        </p>
        {!w ? (
          <Empty>Loading…</Empty>
        ) : (
          <div className="flex gap-6 flex-wrap">
            <Stat label="received" value={w.received} />
            <Stat label="sent" value={w.sent} />
            <Stat label="mutual" value={w.mutual} hint="opens a conversation" />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Nudges ─────────────────────────────────────────────────────────

function Nudges() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [shownType, setShownType] = useState<string>(ib.NUDGE_TYPES[2]);
  const [lastId, setLastId] = useState<string | null>(null);

  const { data: active } = useQuery({
    queryKey: ["ib-nudges-active"],
    queryFn: ib.getActiveNudges,
  });
  const { data: history } = useQuery({
    queryKey: ["ib-nudge-history"],
    queryFn: ib.getNudgeHistory,
  });
  // Admin-only. An ordinary member is refused, and that is worth saying on
  // screen rather than rendering an empty band that looks like zero activity.
  const { data: stats, error: statsError } = useQuery({
    queryKey: ["ib-nudge-stats"],
    queryFn: ib.getNudgeStats,
    retry: false,
  });

  // The active list is served either bare or wrapped depending on the caller;
  // accept both rather than guessing which.
  const raw = active?.data;
  const nudges: ib.ActiveNudge[] = Array.isArray(raw) ? raw : (raw?.nudges ?? []);
  const rows = history?.data ?? [];
  const s = stats?.data;

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setNote(null);
    setErr(null);
    try {
      await fn();
      setNote(ok);
      await qc.invalidateQueries({ queryKey: ["ib-nudge-history"] });
      await qc.invalidateQueries({ queryKey: ["ib-nudge-stats"] });
      await qc.invalidateQueries({ queryKey: ["ib-nudges-active"] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "That did not work");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      {statsError && (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
          Aggregate stats are admin-only, so this is refused for an ordinary
          member — which is the guard working. The server said:{" "}
          <em>{statsError instanceof Error ? statsError.message : "forbidden"}</em>
        </p>
      )}

      {s && (
        <div>
          <div className="flex gap-6 flex-wrap">
            <Stat label="shown" value={s.totalShown} />
            <Stat label="clicked" value={s.totalClicked} />
            <Stat label="dismissed" value={s.totalDismissed} />
            <Stat label="completed" value={s.totalCompleted} />
            {/* Already a rate — do not divide again. */}
            <Stat label="click rate" value={`${s.clickRate}%`} />
            <Stat label="completion rate" value={`${s.completionRate}%`} />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Dismissed and merely-ignored are counted apart on purpose. Without
            that split the completion rate cannot tell "nobody wanted this" from
            "nobody could finish it".
          </p>
        </div>
      )}

      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <p className="text-sm font-medium text-gray-900">
            Active nudges ({nudges.length})
          </p>
          <select
            value={shownType}
            onChange={(e) => setShownType(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1 text-xs"
          >
            {ib.NUDGE_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <Button
            size="sm"
            variant="secondary"
            isLoading={busy}
            onClick={() =>
              run(async () => {
                // Takes a TYPE and MAKES the tracking row; the id it returns is
                // what clicked/dismissed/completed then refer to.
                const res = await ib.trackNudgeShown(shownType);
                setLastId(res.data.nudgeId);
              }, "Recorded as shown — its id is now usable below.")
            }
          >
            Record one shown
          </Button>
        </div>

        {!nudges.length ? (
          <Empty>
            Nothing to nudge about. That is a complete board doing its job, not
            an empty page.
          </Empty>
        ) : (
          <ul className="space-y-2">
            {nudges.map((n) => (
              <li key={n.id} className="border border-gray-200 rounded-xl px-4 py-3 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  {n.nudgeType && <Badge variant="blue">{n.nudgeType.replace(/_/g, " ")}</Badge>}
                  <span className="font-medium text-gray-900">{n.title ?? n.id}</span>
                </div>
                {n.message && <p className="text-gray-700 mt-1">{n.message}</p>}
                <div className="flex gap-3 mt-2">
                  <button
                    disabled={busy}
                    onClick={() => run(() => ib.trackNudgeClicked(n.id), "Clicked.")}
                    className="text-xs text-gray-600 disabled:opacity-50"
                  >
                    clicked
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => run(() => ib.trackNudgeCompleted(n.id), "Completed.")}
                    className="text-xs text-green-700 disabled:opacity-50"
                  >
                    completed
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => run(() => ib.trackNudgeDismissed(n.id), "Dismissed.")}
                    className="text-xs text-red-700 disabled:opacity-50"
                  >
                    dismissed
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {note && <p className="text-sm text-green-700 mt-2">{note}</p>}
        {err && <p className="text-sm text-red-700 mt-2">{err}</p>}
      </div>

      {lastId && (
        <div className="border-t border-gray-200 pt-4">
          <p className="text-sm font-medium text-gray-900 mb-1">
            Last tracked nudge
          </p>
          <p className="text-xs text-gray-500 mb-2">
            <code>{lastId}</code> — the three outcomes below all refer to this
            id, which is why something has to be shown before anything else can
            happen to it.
          </p>
          <div className="flex gap-3">
            <button
              disabled={busy}
              onClick={() => run(() => ib.trackNudgeClicked(lastId), "Clicked.")}
              className="text-xs text-gray-600 disabled:opacity-50"
            >
              clicked
            </button>
            <button
              disabled={busy}
              onClick={() => run(() => ib.trackNudgeCompleted(lastId), "Completed.")}
              className="text-xs text-green-700 disabled:opacity-50"
            >
              completed
            </button>
            <button
              disabled={busy}
              onClick={() => run(() => ib.trackNudgeDismissed(lastId), "Dismissed.")}
              className="text-xs text-red-700 disabled:opacity-50"
            >
              dismissed
            </button>
          </div>
        </div>
      )}

      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm font-medium text-gray-900 mb-2">History ({rows.length})</p>
        {!rows.length ? (
          <Empty>Nothing tracked yet.</Empty>
        ) : (
          <ul className="space-y-1.5">
            {rows.slice(0, 25).map((r, i) => (
              <li
                key={`${r.id}-${i}`}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm flex items-center gap-2 flex-wrap"
              >
                <Badge variant="blue">{r.nudgeType}</Badge>
                <span className="text-xs text-gray-400">
                  {new Date(r.shownAt).toLocaleString()}
                </span>
                {/* Booleans, not timestamps — we know THAT, never WHEN. */}
                <span className="ml-auto flex gap-1.5">
                  {r.clicked && <Badge variant="blue">clicked</Badge>}
                  {r.completedAction && <Badge variant="green">completed</Badge>}
                  {r.dismissed && <Badge variant="gray">dismissed</Badge>}
                  {!r.clicked && !r.completedAction && !r.dismissed && (
                    <Badge variant="gray">ignored</Badge>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Reference ──────────────────────────────────────────────────────

function Reference() {
  const [type, setType] = useState<string>(ib.REFERENCE_TYPES[2]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["ib-reference", type],
    queryFn: () => ib.getReferenceData(type),
  });

  // Bare array for a single type; `all` answers with an object keyed by type.
  const raw = data?.data;
  const items: ib.ReferenceItem[] = Array.isArray(raw) ? raw : [];

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        The vocabularies a board is built from. These are what make a match
        computable at all — two people cannot be found to share a hobby unless
        both picked it from the same list.
      </p>
      <div className="flex gap-1.5 flex-wrap mb-4">
        {ib.REFERENCE_TYPES.map((t) => (
          <button
            key={t}
            onClick={() => setType(t)}
            className={`px-2.5 py-1 rounded-full text-xs ${
              type === t ? "bg-gray-900 text-white" : "border border-gray-300 text-gray-600"
            }`}
          >
            {t.replace(/-/g, " ")}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Empty>Loading…</Empty>
      ) : error ? (
        <p className="text-sm text-red-700">
          {error instanceof Error ? error.message : "Could not load that vocabulary"}
        </p>
      ) : !items.length ? (
        <Empty>Nothing catalogued under {type}.</Empty>
      ) : (
        <>
          <p className="text-xs text-gray-500 mb-2">{items.length} entries</p>
          <div className="flex gap-1.5 flex-wrap">
            {items.slice(0, 120).map((it) => (
              <span key={it.code} title={it.description ?? it.code}>
                <Badge variant="gray">{it.name}</Badge>
              </span>
            ))}
          </div>
        </>
      )}
    </>
  );
}
